import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FaPaperPlane, FaSearch } from "react-icons/fa";
import styles from "./Messages.module.css";
import socketService from "../services/socketService";
import {
  createClientMessageId,
  ensureConversation,
  fetchConversations,
  fetchMessages,
  formatMessageTime,
  formatBubbleTime,
  getInitials,
  groupMessagesByDate,
  markConversationRead,
  normalizeChatMessage,
  sendMessage,
} from "../utils/cmChatApi";
import { publishCmChatUnread } from "../utils/cmChatUnread";

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedPatientUserId, setSelectedPatientUserId] = useState(null);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const activeConversationIdRef = useRef(null);
  const selectedPatientUserIdRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const seenMessageIdsRef = useRef(new Set());

  const selectedConversation =
    conversations.find(
      (conversation) => conversation.patientUserId === selectedPatientUserId,
    ) || null;

  const filteredConversations = conversations.filter((conversation) => {
    if (!search.trim()) return true;
    const query = search.trim().toLowerCase();
    return (
      conversation.patientName?.toLowerCase().includes(query) ||
      conversation.patientEmail?.toLowerCase().includes(query)
    );
  });

  const scrollToBottom = useCallback((behavior = "auto") => {
    const area = messagesAreaRef.current;
    if (!area) return;
    area.scrollTo({ top: area.scrollHeight, behavior });
  }, []);

  const isNearBottom = useCallback(() => {
    const area = messagesAreaRef.current;
    if (!area) return true;
    return area.scrollHeight - area.scrollTop - area.clientHeight < 80;
  }, []);

  const handleMessagesScroll = useCallback(() => {
    stickToBottomRef.current = isNearBottom();
  }, [isNearBottom]);

  const sortConversations = useCallback((rows) => {
    return [...rows].sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bTime - aTime;
    });
  }, []);

  const conversationMatchesMessage = useCallback((row, message) => {
    if (row.conversationId && row.conversationId === message.conversationId) {
      return true;
    }

    return (
      message.senderType === "patient" &&
      String(row.patientUserId) === String(message.senderId)
    );
  }, []);

  const updateSidebarFromMessage = useCallback(
    (message) => {
      const isActiveConversation =
        message.conversationId === activeConversationIdRef.current;
      const isSelectedPatient =
        message.senderType === "patient" &&
        String(selectedPatientUserIdRef.current) === String(message.senderId);

      setConversations((prev) => {
        let matched = false;

        const updated = prev.map((row) => {
          if (!conversationMatchesMessage(row, message)) {
            return row;
          }

          matched = true;

          const unreadCount = message.isSelf
            ? 0
            : isActiveConversation || isSelectedPatient
              ? 0
              : (row.unreadCount || 0) + 1;

          return {
            ...row,
            conversationId: row.conversationId || message.conversationId,
            lastMessagePreview: message.body,
            lastMessageAt: message.createdAt,
            unreadCount,
          };
        });

        if (!matched && message.senderType === "patient") {
          return sortConversations([
            ...updated,
            {
              conversationId: message.conversationId,
              patientUserId: message.senderId,
              patientRecordId: null,
              patientName: "Patient",
              patientEmail: null,
              lastMessagePreview: message.body,
              lastMessageAt: message.createdAt,
              unreadCount: isActiveConversation || isSelectedPatient ? 0 : 1,
            },
          ]);
        }

        return sortConversations(updated);
      });
    },
    [conversationMatchesMessage, sortConversations],
  );

  const clearUnreadInList = useCallback(
    (conversationId, patientUserId = null) => {
      setConversations((prev) =>
        prev.map((row) => {
          const matchesConversation =
            conversationId && row.conversationId === conversationId;
          const matchesPatient =
            patientUserId && row.patientUserId === patientUserId;

          if (!matchesConversation && !matchesPatient) {
            return row;
          }

          return { ...row, unreadCount: 0 };
        }),
      );
    },
    [],
  );

  const loadMessagesForConversation = useCallback(
    async (conversationId, patientUserId = null) => {
      if (!conversationId) return;
      setLoadingMessages(true);
      setError("");

      try {
        const response = await fetchMessages(conversationId, { limit: 100 });
        if (response?.success) {
          const rows = response.data?.messages || [];
          setMessages(rows);

          const lastMessage = rows.length ? rows[rows.length - 1] : null;
          await markConversationRead(conversationId, lastMessage?.id);
          clearUnreadInList(conversationId, patientUserId);
        }
      } catch (messageError) {
        console.error("Error loading messages:", messageError);
        setError("Unable to load messages.");
      } finally {
        setLoadingMessages(false);
      }
    },
    [clearUnreadInList],
  );

  const openConversation = useCallback(
    async (conversationRow) => {
      if (!conversationRow?.patientUserId) return;

      setSelectedPatientUserId(conversationRow.patientUserId);
      setMessages([]);
      stickToBottomRef.current = true;
      clearUnreadInList(
        conversationRow.conversationId,
        conversationRow.patientUserId,
      );

      try {
        let conversationId = conversationRow.conversationId;

        if (!conversationId) {
          const response = await ensureConversation(
            conversationRow.patientUserId,
          );
          if (response?.success) {
            conversationId = response.data.conversationId;
            setConversations((prev) =>
              prev.map((row) =>
                row.patientUserId === conversationRow.patientUserId
                  ? { ...row, ...response.data, unreadCount: 0 }
                  : row,
              ),
            );
          }
        }

        if (conversationId) {
          setActiveConversationId(conversationId);
          await loadMessagesForConversation(
            conversationId,
            conversationRow.patientUserId,
          );
        }
      } catch (openError) {
        console.error("Error opening conversation:", openError);
        setError("Unable to open conversation.");
      }
    },
    [clearUnreadInList, loadMessagesForConversation],
  );

  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    setError("");
    try {
      const response = await fetchConversations();
      if (response?.success) {
        const rows = response.data || [];
        setConversations(sortConversations(rows));

        if (rows.length && !hasInitializedRef.current) {
          hasInitializedRef.current = true;
          await openConversation(rows[0]);
        }
      }
    } catch (listError) {
      console.error("Error loading conversations:", listError);
      setError("Unable to load conversations.");
    } finally {
      setLoadingList(false);
    }
  }, [openConversation]);

  useEffect(() => {
    publishCmChatUnread(conversations);
  }, [conversations]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    selectedPatientUserIdRef.current = selectedPatientUserId;
  }, [selectedPatientUserId]);

  useEffect(() => {
    const cmChatSocket = socketService.connectCmChat();

    const handleIncomingMessage = (rawMessage) => {
      const message = normalizeChatMessage(rawMessage);
      if (seenMessageIdsRef.current.has(message.id)) {
        return;
      }
      seenMessageIdsRef.current.add(message.id);

      const isActiveConversation =
        message.conversationId === activeConversationIdRef.current;

      if (isActiveConversation) {
        setMessages((prev) => {
          if (prev.some((item) => item.id === message.id)) {
            return prev;
          }
          return [...prev, message];
        });

        if (isNearBottom()) {
          stickToBottomRef.current = true;
        }

        if (!message.isSelf) {
          markConversationRead(message.conversationId, message.id).catch(
            () => {},
          );
        }
      }

      updateSidebarFromMessage(message);
    };

    const handleRead = (payload) => {
      clearUnreadInList(payload.conversationId);
    };

    cmChatSocket.on("cm-chat:message", handleIncomingMessage);
    cmChatSocket.on("cm-chat:read", handleRead);

    return () => {
      cmChatSocket.off("cm-chat:message", handleIncomingMessage);
      cmChatSocket.off("cm-chat:read", handleRead);
    };
  }, [clearUnreadInList, updateSidebarFromMessage, isNearBottom]);

  useEffect(() => {
    if (!activeConversationId) return undefined;

    const cmChatSocket = socketService.connectCmChat();

    const joinRoom = () => {
      cmChatSocket.emit("cm-chat:join", {
        conversationId: activeConversationId,
      });
    };

    joinRoom();
    cmChatSocket.on("connect", joinRoom);

    return () => {
      cmChatSocket.emit("cm-chat:leave", {
        conversationId: activeConversationId,
      });
      cmChatSocket.off("connect", joinRoom);
    };
  }, [activeConversationId]);

  useLayoutEffect(() => {
    if (loadingMessages) return;
    if (stickToBottomRef.current) {
      scrollToBottom("auto");
    }
  }, [messages, loadingMessages, scrollToBottom]);

  const handleSend = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || !activeConversationId || sending) return;

    const clientMessageId = createClientMessageId();
    setSending(true);
    setDraft("");
    stickToBottomRef.current = true;

    try {
      const response = await sendMessage(
        activeConversationId,
        text,
        clientMessageId,
      );
      if (response?.success) {
        const message = response.data;
        seenMessageIdsRef.current.add(message.id);
        setMessages((prev) => {
          const withoutDuplicate = prev.filter((item) => item.id !== message.id);
          return [...withoutDuplicate, message];
        });

        setConversations((prev) =>
          sortConversations(
            prev.map((row) =>
              row.conversationId === activeConversationId
                ? {
                    ...row,
                    lastMessagePreview: message.body,
                    lastMessageAt: message.createdAt,
                    unreadCount: 0,
                  }
                : row,
            ),
          ),
        );
      }
    } catch (sendError) {
      console.error("Error sending message:", sendError);
      setDraft(text);
      setError("Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend(event);
    }
  };

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <div className={`app-page ${styles.page}`}>
      <div className={styles.container}>
        <aside className={styles.listPanel}>
          <div className={styles.listHeader}>
            <h1 className={styles.listTitle}>Messages</h1>
            <div className={styles.searchWrap}>
              <FaSearch className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search patient"
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          <div className={styles.conversationList}>
            {loadingList ? (
              <p className={styles.emptyState}>Loading conversations...</p>
            ) : filteredConversations.length === 0 ? (
              <p className={styles.emptyState}>No linked patients found.</p>
            ) : (
              filteredConversations.map((conversation) => (
                <button
                  key={conversation.patientUserId}
                  type="button"
                  className={`${styles.conversationItem} ${
                    selectedPatientUserId === conversation.patientUserId
                      ? styles.conversationItemActive
                      : ""
                  }`}
                  onClick={() => openConversation(conversation)}
                >
                  <div className={styles.avatar}>
                    {getInitials(conversation.patientName)}
                  </div>
                  <div className={styles.conversationBody}>
                    <div className={styles.conversationTop}>
                      <p className={styles.patientName}>
                        {conversation.patientName}
                      </p>
                      <div className={styles.conversationMeta}>
                        <span className={styles.timestamp}>
                          {formatMessageTime(conversation.lastMessageAt)}
                        </span>
                        {conversation.unreadCount > 0 &&
                          selectedPatientUserId !==
                            conversation.patientUserId && (
                            <span className={styles.unreadBadge}>
                              {conversation.unreadCount}
                            </span>
                          )}
                      </div>
                    </div>
                    <div className={styles.previewRow}>
                      <p className={styles.previewText}>
                        {conversation.lastMessagePreview ||
                          "Start a conversation"}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        <section className={styles.chatPanel}>
          {selectedConversation ? (
            <>
              <header className={styles.chatHeader}>
                <div className={styles.chatHeaderLeft}>
                  <div className={styles.avatar}>
                    {getInitials(selectedConversation.patientName)}
                  </div>
                  <h2 className={styles.chatPatientName}>
                    {selectedConversation.patientName}
                  </h2>
                </div>
              </header>

              <div
                className={styles.messagesArea}
                ref={messagesAreaRef}
                onScroll={handleMessagesScroll}
              >
                {error && <p className={styles.errorText}>{error}</p>}
                {loadingMessages ? (
                  <p className={styles.emptyState}>Loading messages...</p>
                ) : groupedMessages.length === 0 ? (
                  <p className={styles.emptyState}>
                    No messages yet. Start the conversation below.
                  </p>
                ) : (
                  groupedMessages.map((group) => (
                    <div key={group.date} className={styles.messageGroup}>
                      <div className={styles.dateDivider}>
                        <span className={styles.datePill}>{group.date}</span>
                      </div>
                      {group.items.map((message) => (
                        <div
                          key={message.id}
                          className={`${styles.messageRow} ${
                            message.isSelf
                              ? styles.messageRowOutgoing
                              : styles.messageRowIncoming
                          }`}
                        >
                          <div
                            className={`${styles.bubble} ${
                              message.isSelf
                                ? styles.bubbleOutgoing
                                : styles.bubbleIncoming
                            }`}
                          >
                            <span className={styles.bubbleText}>
                              {message.body}
                            </span>
                            <span
                              className={`${styles.bubbleTime} ${
                                message.isSelf
                                  ? styles.bubbleTimeOutgoing
                                  : styles.bubbleTimeIncoming
                              }`}
                            >
                              {formatBubbleTime(message.createdAt)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form className={styles.composeArea} onSubmit={handleSend}>
                <div className={styles.composeBox}>
                  <textarea
                    className={styles.composeInput}
                    placeholder="Type something..."
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <div className={styles.composeFooter}>
                    <p className={styles.composeHint}>
                      Enter to send • Shift + Enter for new line
                    </p>
                    <button
                      type="submit"
                      className={styles.sendButton}
                      aria-label="Send message"
                      disabled={sending || !draft.trim()}
                    >
                      <FaPaperPlane />
                    </button>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <div className={styles.emptyChat}>
              <p>Select a patient to view messages.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Messages;
